import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount, getPeriodDateRange } from '../../components/common/AdvancedFilter/periodUtils';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import { formatDateDDMMYYYY } from '../../utils/formatters';

// Serialize filter value (array or string) to API param: "all" or "id1,id2,..."
const toFilterParam = (v) => {
  if (Array.isArray(v)) {
    const clean = v.filter((x) => x != null && x !== '' && x !== 'all');
    return clean.length === 0 ? 'all' : clean.join(',');
  }
  return v == null || v === '' ? 'all' : String(v);
};

const DEFAULT_PAGE_SIZE = 10;

// Map frontend column keys to backend sort property names (Spring sort param) — backend-based
const PROPOSALS_REPORT_SORT_KEY_TO_BACKEND = {
  proposal: 'proposalCode',
  client: 'companyName',
  proposalDate: 'proposalDate',
  validUntil: 'validUntil',
  statusDesc: 'statusDesc',
  statusProposalDesc: 'statusProposalDesc',
  amount: 'grandTotal',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  createdBy: 'createdBy',
  assignedTo: 'assignedTo',
  department: 'department',
  sector: 'sectorName',
  projectStatus: 'projectStatusDesc',
  stageUpdatedAt: 'stageUpdatedAt',
  proposalStage: 'proposalStageId',
  partnerType: 'partnerType',
};

/** Normalize backend projectStatusDesc to "Converted" | "Not Converted" */
function normalizeProjectStatus(raw) {
  if (raw == null || String(raw).trim() === '') return 'Not Converted';
  const val = String(raw).toLowerCase();
  if (['converted', '1', 'true', 'done', 'closed', 'success'].includes(val)) return 'Converted';
  if (['not converted', '0', 'false', 'open', 'pending', 'draft'].includes(val)) return 'Not Converted';
  return val ? String(raw).charAt(0).toUpperCase() + String(raw).slice(1).toLowerCase() : 'Not Converted';
}

/** Convert the original proposal total to IDR using its creation-time rate. */
const getProposalAmountInIdr = (item) => {
  const sourceAmount = Number(item.grandTotalDisplayCurrency ?? item.grandTotal ?? 0);
  if (!Number.isFinite(sourceAmount)) return 0;

  const currencyCode = String(item.displayCurrencyCode || 'IDR').trim().toUpperCase();
  if (currencyCode === 'IDR') return sourceAmount;

  const creationRate = Number(item.exchangeRateAtCreation);
  return Number.isFinite(creationRate) && creationRate > 0
    ? sourceAmount * creationRate
    : sourceAmount;
};

/** Same numeric ids as `Proposals/index.jsx` — backend matches `status` / `statusProposal` codes and lowercased *_Desc. */
const PROPOSALS_REPORT_APPROVAL_STATUS_OPTIONS = [
  { id: 1, name: 'Draft' },
  { id: 2, name: 'Approved' },
  { id: 3, name: 'Need Approval' },
  { id: 4, name: 'Rejected' },
  { id: 5, name: 'Revision' }
];

const PROPOSALS_REPORT_PROPOSAL_STATUS_OPTIONS = [
  { id: 1, name: 'Draft' },
  { id: 2, name: 'Send to Client' },
  { id: 3, name: 'Accepted' },
  { id: 4, name: 'Rejected' },
  { id: 5, name: 'Feedback' }
];

const PROPOSALS_REPORT_PROJECT_STATUS_OPTIONS = [
  { id: 1, name: 'Not Converted' },
  { id: 2, name: 'Converted' }
];

const emptyProposalReportFilters = () => ({
  period: '',
  startDate: '',
  endDate: '',
  assignedTo: [],
  createdBy: [],
  department: [],
  sector: [],
  standards: [],
  source: [],
  service: [],
  proposalStage: [],
  approvalStatus: [],
  proposalStatus: [],
  projectStatus: '',
  proposalDateFrom: '',
  proposalDateTo: '',
  validUntilFrom: '',
  validUntilTo: '',
  stageUpdateFrom: '',
  stageUpdateTo: '',
  updatedAtFrom: '',
  updatedAtTo: '',
  branchId: [],
  taggingIds: [],
});

const ProposalsReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadPersistedState, persistState } = usePersistedFilters('proposals_report');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [proposalsData, setProposalsData] = useState([]);

  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Server-side sort (ascending/descending handled by backend)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Totals row (page total + grand total across all filtered proposals)
  const [totalSums, setTotalSums] = useState({ totalAmount: 0, pageTotal: 0 });
  const [proposalCurrencyCode, setProposalCurrencyCode] = useState('IDR');

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();

  // Helper function to check proposal-specific capabilities
  const canProposal = (capabilityKey) => can(capabilityKey, 'proposal');

  // Keep report totals in the same IDR base currency as the Proposals list.
  const proposalBaseAmount = (proposal) =>
    parseFloat(proposal.amount) ||
    parseFloat(proposal.grandTotalBaseCurrency) ||
    parseFloat(proposal.grandTotal) ||
    0;

  // Unified filter state: in-progress (panel open) + committed (applied to API)
  const [filterData, setFilterData] = useState(emptyProposalReportFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyProposalReportFilters);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // Filter options state (aligned with Proposals list: `/crm/proposals/filter-options`)
  const [filterOptions, setFilterOptions] = useState({
    sectors: [],
    proposalStages: [],
    standards: [],
    assignedToUsers: [],
    createdByUsers: []
  });

  // Filter data state for dropdowns
  const [sourcesData, setSourcesData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [businessUnitsData, setBusinessUnitsData] = useState([]);
  const [salesUsersData, setSalesUsersData] = useState([]);
  // Branch (current user's branches, same source as Proposals/index.jsx) + Tagging options
  // (company + proposal, same pattern as Proposals/index.jsx) for the Branch/Tagging filters
  const [branches, setBranches] = useState([]);
  const [taggingOptions, setTaggingOptions] = useState([]);
  const [proposalTaggingOptions, setProposalTaggingOptions] = useState([]);
  // HRIS user lookup: userId → { deptId, status } for cascade + inactive badge
  const [proposalRptUserLookup, setProposalRptUserLookup] = useState(new Map());
  useEffect(() => {
    api.get('/md/internal-users/option-internal-user')
      .then(res => {
        const map = new Map();
        (res.data || []).forEach(u => {
          const deptId = u.departmentId || u.departementId;
          if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
        });
        setProposalRptUserLookup(map);
      })
      .catch(() => {});
  }, []);

  // Determine whether a user can see people/department filters
  const canViewGlobal = canProposal('view_global');
  const canViewDepartment = canProposal('view_department');

  // Parse comma-separated URL param to array for multiselect
  const parseParamToArray = (v) => {
    if (!v || v === 'all') return [];
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  };

  // Initialize filters from URL query parameters — re-runs when location.search changes
  // so navigating from dashboard with different params always applies the new filters.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasUrlParams = Array.from(searchParams.keys()).length > 0;

    if (hasUrlParams) {
      // Helper: resolve a dashboard period-key value (e.g. 'this_month') + custom date params
      // to an explicit { from, to } date range used by ProposalsReports filter state.
      const resolvePeriodToRange = (periodVal, startKey, endKey) => {
        if (!periodVal || periodVal === 'all') return { from: '', to: '' };
        if (periodVal === 'custom') {
          return {
            from: searchParams.get(startKey) || '',
            to: searchParams.get(endKey) || '',
          };
        }
        const { startDate, endDate } = getPeriodDateRange(periodVal);
        return { from: startDate, to: endDate };
      };

      // Main period (generic or createdAt-keyed navigation from dashboard)
      const mainPeriod = searchParams.get('period') || searchParams.get('createdAt') || '';
      const mainStart = searchParams.get('customStartDate') || searchParams.get('createdAtStartDate') || searchParams.get('startDate') || '';
      const mainEnd = searchParams.get('customEndDate') || searchParams.get('createdAtEndDate') || searchParams.get('endDate') || '';

      // Proposal-date-keyed navigation (periodKey: 'proposalDate')
      const proposalDateKey = searchParams.get('proposalDate') || '';
      const proposalRange = resolvePeriodToRange(proposalDateKey, 'proposalDateStartDate', 'proposalDateEndDate');

      // Stage-update-keyed navigation (periodKey: 'stageUpdatePeriod')
      const stageUpdateKey = searchParams.get('stageUpdatePeriod') || '';
      const stageRange = resolvePeriodToRange(stageUpdateKey, 'stageUpdateStartDate', 'stageUpdateEndDate');

      // Updated-at-keyed navigation (periodKey: 'updatedAt')
      const updatedAtKey = searchParams.get('updatedAt') || '';
      const updatedAtRange = resolvePeriodToRange(updatedAtKey, 'updatedAtStartDate', 'updatedAtEndDate');

      const urlFilters = {
        period: mainPeriod === 'all' ? '' : mainPeriod,
        startDate: mainPeriod === 'custom' ? mainStart : '',
        endDate: mainPeriod === 'custom' ? mainEnd : '',
        // Accept both report-native names and dashboard alias names
        assignedTo: parseParamToArray(searchParams.get('assignedTo') || searchParams.get('people')),
        department: parseParamToArray(searchParams.get('department') || searchParams.get('businessUnits')),
        sector: parseParamToArray(searchParams.get('sector')),
        standards: parseParamToArray(searchParams.get('standards') || searchParams.get('standard')),
        source: parseParamToArray(searchParams.get('source')),
        service: parseParamToArray(searchParams.get('service')),
        proposalStage: parseParamToArray(searchParams.get('proposalStage')),
        // Accept both native names and dashboard aliases (statusApproval / statusProposal)
        approvalStatus: parseParamToArray(searchParams.get('approvalStatus') || searchParams.get('statusApproval')),
        proposalStatus: parseParamToArray(searchParams.get('proposalStatus') || searchParams.get('statusProposal')),
        projectStatus: searchParams.get('projectStatus') || '',
        createdBy: parseParamToArray(searchParams.get('createdBy')),
        // Resolve dimension-specific period keys to their respective date range fields
        proposalDateFrom: proposalRange.from || searchParams.get('proposalDateFrom') || '',
        proposalDateTo: proposalRange.to || searchParams.get('proposalDateTo') || '',
        validUntilFrom: searchParams.get('validUntilFrom') || '',
        validUntilTo: searchParams.get('validUntilTo') || '',
        stageUpdateFrom: stageRange.from || searchParams.get('stageUpdateFrom') || '',
        stageUpdateTo: stageRange.to || searchParams.get('stageUpdateTo') || '',
        updatedAtFrom: updatedAtRange.from || searchParams.get('updatedAtFrom') || '',
        updatedAtTo: updatedAtRange.to || searchParams.get('updatedAtTo') || '',
      };
      setFilterData(urlFilters);
      setAppliedFilters(urlFilters);
    } else {
      // No URL params — restore from sessionStorage if available
      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? emptyProposalReportFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;
        setFilterData(restoredFilters);
        setAppliedFilters(restoredFilters);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);
      }
    }

    setUrlParamsProcessed(true);
  }, [location.search]);

  // Fetch filter options from API (branch options are the current user's branches, same source
  // as Proposals/index.jsx; tagging options cover both company and proposal tags)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [filterRes, sectorsRes, standardsRes, sourcesRes, depsRes, userBranchesRes, taggingRes, proposalTaggingRes] = await Promise.all([
          api.get('/crm/proposals/filter-options').catch(() => ({ data: { assignedToUsers: [], createdByUsers: [], proposalStages: [] } })),
          api.get('/md/sector').catch(() => ({ data: [] })),
          api.get('/md/standards').catch(() => ({ data: [] })),
          api.get('/md/company-source').catch(() => ({ data: [] })),
          api.get('/md/departement').catch(() => ({ data: [] })),
          user?.id ? api.get(`/user-branches/user/${user.id}`).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }).catch(() => ({ data: [] })),
          api.get('/md/tagging', { params: { moduleKey: 'proposal' } }).catch(() => ({ data: [] }))
        ]);
        setFilterOptions({
          sectors: (sectorsRes.data || []).map(s => ({ id: s.id, name: s.name })),
          proposalStages: (filterRes.data.proposalStages || []).map(p => ({ id: p.id ?? p, name: p.name ?? p })),
          standards: (standardsRes.data || []).map(s => ({ id: s.id, name: s.name })),
          assignedToUsers: filterRes.data.assignedToUsers || [],
          createdByUsers: filterRes.data.createdByUsers || []
        });
        setSourcesData((sourcesRes.data || []).map(s => ({ id: s.id, name: s.name })));
        setBusinessUnitsData((depsRes.data || []).map(d => ({ id: d.id, name: d.name })));
        const branchData = userBranchesRes.data?.data ?? userBranchesRes.data ?? [];
        setBranches(Array.isArray(branchData) ? branchData : []);
        setTaggingOptions(taggingRes.data || []);
        setProposalTaggingOptions(proposalTaggingRes.data || []);
      } catch (err) {
        console.error('Error fetching filter options:', err);
        setFilterOptions({
          sectors: [],
          proposalStages: [],
          standards: [],
          assignedToUsers: [],
          createdByUsers: []
        });
      }
    };
    fetchFilterOptions();
  }, [user?.id]); // Refetch when user is available to load user's branches for filter

  const fetchServicesData = async () => {
    try {
      const response = await api.get('/crm/services/options');
      setServicesData((response.data || []).map(s => ({ id: s.id ?? s.value, name: s.name ?? s.label })));
    } catch (error) {
      console.error('Error fetching services data:', error);
      setServicesData([]);
    }
  };

  useEffect(() => {
    fetchServicesData();
  }, []);

  // Fetch sales users for assigned-to filter (department-filtered when view_department)
  const fetchSalesUsersData = async () => {
    try {
      const response = await api.get('/dashboard/sales-users');
      if (response.data && response.data.salesUsers) {
        let users = response.data.salesUsers;
        if (canViewDepartment && !canViewGlobal && user?.departmentId) {
          users = users.filter(u => u.departmentId != null && String(u.departmentId) === String(user.departmentId));
        }
        setSalesUsersData(users);
      }
    } catch (error) {
      console.error('Error fetching sales users data:', error);
      setSalesUsersData([]);
    }
  };

  // Load sales users when capabilities resolve
  useEffect(() => {
    if (!capabilitiesLoading) {
      fetchSalesUsersData();
    }
  }, [capabilitiesLoading, user?.departmentId]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [appliedFilters]);

  // Reload when capabilities ready + URL params processed, and on filter/pagination/sort/search changes
  useEffect(() => {
    if (!capabilitiesLoading && urlParamsProcessed) {
      loadProposals(page, size, sortConfig, appliedFilters);
    }
  }, [capabilitiesLoading, urlParamsProcessed, page, size, sortConfig, searchQuery, appliedFilters]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Tagging filter matches company tags and proposal tags — same approach as the fixed
  // Proposals/index.jsx: company tags ARE filterable server-side via `taggingId` (backend has no
  // equivalent for proposal-specific tags). Company-only selections go straight to the backend
  // for an exact count; any proposal tag forces a full-set client pass (see loadProposals below)
  // so the OR-match and resulting total are computed over every matching row, not just one page.
  const applyTaggingFilter = (rows, taggingIds) => {
    const effectiveTaggingIds = Array.isArray(taggingIds) ? taggingIds.filter(Boolean) : [];
    if (effectiveTaggingIds.length === 0) return rows;
    return rows.filter(p => {
      const companyNames = p.tagging ? p.tagging.split(',').map(s => s.trim()).filter(Boolean) : [];
      const rowCompanyTagIds = companyNames.map(name => {
        const found = taggingOptions.find(opt => opt.name === name);
        return found ? String(found.id) : null;
      }).filter(Boolean);

      const proposalRaw = p.proposalTagging ?? p.proposal_tagging ?? '';
      const proposalNames = typeof proposalRaw === 'string' ? proposalRaw.split(',').map(s => s.trim()).filter(Boolean) : [];
      const rowProposalTagIds = proposalNames.map(name => {
        const found = proposalTaggingOptions.find(opt => opt.name === name);
        return found ? `proposal-${found.id}` : null;
      }).filter(Boolean);

      return effectiveTaggingIds.some(id => rowCompanyTagIds.includes(id) || rowProposalTagIds.includes(id));
    });
  };

  const loadProposals = async (pageNum = page, pageSize = size, sortConfigOverride = null, filtersOverride = null) => {
    const f = filtersOverride ?? appliedFilters;
    setIsLoading(true);
    try {
      // Split tagging selection: company tags can go straight to the backend's `taggingId` param
      // for an exact filtered count; any proposal tag forces a full-set client-side pass below,
      // since sending `taggingId` would AND-restrict the query and silently drop proposals that
      // only match on the proposal tag (the two are meant to OR together).
      const effectiveTaggingIds = Array.isArray(f.taggingIds) ? f.taggingIds.filter(Boolean) : [];
      const companyTagIds = effectiveTaggingIds.filter((id) => !String(id).startsWith('proposal-'));
      const proposalTagIds = effectiveTaggingIds.filter((id) => String(id).startsWith('proposal-'));
      const needsClientSideTaggingPass = proposalTagIds.length > 0;

      // Build query parameters from appliedFilters
      const params = new URLSearchParams();
      if (needsClientSideTaggingPass) {
        // Fetch every row matching the other filters so the tagging OR-match + resulting count
        // are computed over the true full set, not just whatever page the backend would return —
        // we paginate the filtered result ourselves below.
        params.set('page', '0');
        params.set('size', '100000');
      } else {
        params.set('page', String(pageNum));
        params.set('size', String(pageSize));
        if (companyTagIds.length > 0) params.set('taggingId', companyTagIds.join(','));
      }

      // ✅ Permission-based filtering hierarchy (same as Proposals page):
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by user's department
      // 3. view_own → filter by user ID (assigned to only)
      // NOTE: Don't override if user explicitly selects people or department filter

      if (!canProposal('view_global')) {
        if (canProposal('view_department')) {
          const assignedToEmpty = !f.assignedTo?.length;
          const departmentEmpty = !f.department?.length;
          if (assignedToEmpty && departmentEmpty && user?.departmentId) {
            params.append('viewDepartmentId', user.departmentId);
          }
        } else if (canProposal('view_own')) {
          if (!f.assignedTo?.length && user?.id) {
            params.append('viewOwnUserId', user.id);
          }
        }
      }

      const assignedToParam = toFilterParam(f.assignedTo);
      const departmentParam = toFilterParam(f.department);
      const sectorParam = toFilterParam(f.sector);
      const sourceParam = toFilterParam(f.source);
      const standardsParam = toFilterParam(f.standards);
      const serviceParam = toFilterParam(f.service);
      const proposalStageParam = toFilterParam(f.proposalStage);
      const createdByParam = toFilterParam(f.createdBy);
      const branchIdParam = toFilterParam(f.branchId);

      if (assignedToParam !== 'all') params.append('assignedTo', assignedToParam);
      if (departmentParam !== 'all') params.append('department', departmentParam);
      if (sectorParam !== 'all') params.append('sector', sectorParam);
      if (sourceParam !== 'all') params.append('source', sourceParam);
      if (standardsParam !== 'all') params.append('standards', standardsParam);
      if (serviceParam !== 'all') params.append('service', serviceParam);
      if (proposalStageParam !== 'all') params.append('proposalStage', proposalStageParam);
      if (createdByParam !== 'all') params.append('createdBy', createdByParam);
      // Backend branchId is a single Long, not comma-separated — same constraint the Branch
      // filter already has on Proposals/index.jsx (multi-select UI, but only the first value is
      // meaningfully honored server-side if more than one is picked).
      if (branchIdParam !== 'all') params.append('branchId', branchIdParam);

      if (Array.isArray(f.approvalStatus) && f.approvalStatus.length > 0) {
        params.append('approvalStatus', f.approvalStatus.map(String).join(','));
      }
      if (Array.isArray(f.proposalStatus) && f.proposalStatus.length > 0) {
        params.append('proposalStatus', f.proposalStatus.map(String).join(','));
      }
      if (f.projectStatus != null && f.projectStatus !== '' && String(f.projectStatus) !== 'all') {
        params.append('projectStatus', String(f.projectStatus));
      }
      if (searchQuery && searchQuery.trim()) params.append('search', searchQuery.trim());

      // Period → stageUpdateFrom/stageUpdateTo, matching Proposals/index.jsx's mapping (period
      // filters by stage_updated_at to match Sales Funnel pipeline logic, not createdAt) — an
      // explicit Stage Update Date filter (below) overrides the period-derived range, same
      // precedence as the non-report Proposals page.
      const { startDate: periodStart, endDate: periodEnd } = getActivePeriodRange(f);
      const effectiveStageUpdateFrom = f.stageUpdateFrom || periodStart;
      const effectiveStageUpdateTo = f.stageUpdateTo || periodEnd;
      if (effectiveStageUpdateFrom) params.append('stageUpdateFrom', effectiveStageUpdateFrom);
      if (effectiveStageUpdateTo) params.append('stageUpdateTo', effectiveStageUpdateTo);

      // Individual date range fields
      if (f.proposalDateFrom) params.append('proposalDateFrom', f.proposalDateFrom);
      if (f.proposalDateTo) params.append('proposalDateTo', f.proposalDateTo);
      if (f.validUntilFrom) params.append('validUntilFrom', f.validUntilFrom);
      if (f.validUntilTo) params.append('validUntilTo', f.validUntilTo);
      if (f.updatedAtFrom) params.append('updatedAtFrom', f.updatedAtFrom);
      if (f.updatedAtTo) params.append('updatedAtTo', f.updatedAtTo);

      // Server-side sort: backend expects sort=propertyName,direction
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && PROPOSALS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = PROPOSALS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.set('sort', `${backendSortProperty},${effectiveSort.direction}`);
      }

      const url = `/crm/proposals?${params.toString()}`;
      const response = await api.get(url);
      const data = response.data;
      let list = Array.isArray(data) ? data : (data?.content ?? data?.data ?? []);
      const currencyCodeCache = new Map();
      const mapProposal = async (item) => {
        let currencyCode = String(item.displayCurrencyCode || '').trim().toUpperCase();
        if (!currencyCode && item.displayCurrencyId) {
          const currencyId = String(item.displayCurrencyId);
          if (!currencyCodeCache.has(currencyId)) {
            currencyCodeCache.set(currencyId,
              api.get(`/crm/currencies/${item.displayCurrencyId}`)
                .then(res => String(res.data?.currencyCode || '').trim().toUpperCase())
                .catch(() => '')
            );
          }
          currencyCode = await currencyCodeCache.get(currencyId);
        }
        currencyCode = currencyCode || 'IDR';
        const effectiveRate = currencyCode === 'IDR' ? 1 : Number(item.exchangeRateAtCreation);
        const amountItem = {
          ...item,
          displayCurrencyCode: currencyCode,
          ...(effectiveRate > 0 ? { exchangeRateAtCreation: effectiveRate } : {})
        };

        return {
          ...amountItem,
          proposal: item.proposalCode,
          client: item.companyName,
          // Amount is displayed and totaled in IDR using the saved creation-time rate.
          amount: getProposalAmountInIdr(amountItem),
          sector: item.sectorName || item.sector || item.sector?.name || '-',
          statusDesc: item.statusDesc === 'APPROVED' ? 'Approved' :
            item.statusDesc === 'REJECTED' ? 'Rejected' :
              item.statusDesc === 'REVISION' ? 'Revision' :
                item.statusDesc === 'NEED_APPROVAL' ? 'Need Approval' :
                  item.statusDesc || 'Draft',
          statusProposalDesc: item.statusProposalDesc === 'ACCEPTED' ? 'Accepted' :
            item.statusProposalDesc === 'REJECTED' ? 'Rejected' :
              item.statusProposalDesc === 'FEEDBACK' ? 'Feedback' :
                item.statusProposalDesc === 'SEND_TO_CLIENT' ? 'Send to Client' :
                  item.statusProposalDesc === 'Send to Client' ? 'Send to Client' :
                    item.statusProposalDesc || 'Draft',
          projectStatus: normalizeProjectStatus(item.projectStatusDesc),
          proposalStageId: item.proposalStageId || item.proposalStage?.id || item.stageId || null,
          proposalStage: item.proposalStageName || item.proposalStage || null
        };
      };

      // Backend returned Page<T>: use list as-is and set pagination state
      const isPagedResponse = data && typeof data.totalElements === 'number';
      if (isPagedResponse) {
        const mappedData = (await Promise.all(list.map(mapProposal)))
          .map(p => ({ ...p, projectStatus: p.projectStatus || 'Not Converted' }));

        if (needsClientSideTaggingPass) {
          // `mappedData` here is the FULL matching set (page=0&size=100000 above), so filtering
          // + counting it gives an accurate total instead of only reflecting one backend page.
          const fullMatch = applyTaggingFilter(mappedData, f.taggingIds);
          const clientTotalElements = fullMatch.length;
          const clientTotalPages = Math.max(1, Math.ceil(clientTotalElements / pageSize));
          const displayData = fullMatch.slice(pageNum * pageSize, (pageNum + 1) * pageSize);

          setTotalElements(clientTotalElements);
          setTotalPages(clientTotalPages);
          setProposalsData(displayData);
          setTotalSums({
            totalAmount: fullMatch.reduce((acc, p) => acc + proposalBaseAmount(p), 0),
            pageTotal: displayData.reduce((acc, p) => acc + proposalBaseAmount(p), 0)
          });
          return;
        }

        // No proposal-tag filtering needed — the backend already applied `taggingId` (if any
        // company tags were selected) and paginated/counted correctly, so no client pass required.
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
        setProposalsData(mappedData);

        const backendTotal = data?.totalAmount != null ? Number(data.totalAmount) : null;
        const backendPageTotal = data?.pageTotal != null ? Number(data.pageTotal) : null;
        setTotalSums({
          totalAmount: backendTotal ?? mappedData.reduce((acc, p) => acc + proposalBaseAmount(p), 0),
          pageTotal: backendPageTotal ?? mappedData.reduce((acc, p) => acc + proposalBaseAmount(p), 0)
        });
        return;
      }

      let dataToFilter = list;

      const mappedData = await Promise.all(dataToFilter.map(mapProposal));

      const displayData = applyTaggingFilter(
        mappedData.map(p => ({ ...p, projectStatus: p.projectStatus || 'Not Converted' })),
        f.taggingIds
      );
      setTotalElements(displayData.length);
      setTotalPages(1);
      setProposalsData(displayData);
      setTotalSums({
        totalAmount: displayData.reduce((acc, p) => acc + proposalBaseAmount(p), 0),
        pageTotal: displayData.reduce((acc, p) => acc + proposalBaseAmount(p), 0)
      });
    } catch (error) {
      console.error('Error loading proposals:', error);
      showAlert('danger', 'Failed to load proposals');
      setTotalSums({ totalAmount: 0, pageTotal: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  // AdvancedFilterPanel handlers
  const handleFilterChange = (key, value) => {
    setFilterData(prev => ({ ...prev, [key]: value }));
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: searchQuery, page: 0, size });
    loadProposals(0, size, sortConfig, applied);
  };

  const handleFilterReset = () => {
    const empty = emptyProposalReportFilters();
    setFilterData(empty);
    setAppliedFilters(empty);
    setPage(0);
    persistState({ filters: empty, search: '', page: 0, size });
    loadProposals(0, size, sortConfig, empty);
  };

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'JPY': return '¥';
      case 'SGD': return 'S$';
      default: return 'Rp';
    }
  };

  const formatAmount = (amount, currencyCode = 'IDR') => {
    if (!amount || amount === 0) {
      return `${getCurrencySymbol(currencyCode)} 0`;
    }

    const symbol = getCurrencySymbol(currencyCode);
    const formattedAmount = amount.toLocaleString('id-ID');
    return `${symbol} ${formattedAmount}`;
  };

  const formatCurrency = (amount, currencyCode = proposalCurrencyCode) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Helper function to get proposal stage name
  const getProposalStageName = (row) => {
    const id = row?.proposalStageId;
    const found = id != null ? (filterOptions?.proposalStages || []).find(ps => ps.id === parseInt(id)) : null;
    return found?.name || row.proposalStage || 'Not Set';
  };

  // Resolved assigned-to options: prefer API data, fall back to salesUsersData; dept-filtered when view_department
  const assignedToOptions = useMemo(() => {
    const raw = filterOptions.assignedToUsers?.length ? filterOptions.assignedToUsers : salesUsersData;
    if (!canViewDepartment || canViewGlobal || !user?.departmentId) return raw;
    return raw.filter(u => u.departmentId != null && String(u.departmentId) === String(user.departmentId));
  }, [filterOptions.assignedToUsers, salesUsersData, canViewDepartment, canViewGlobal, user?.departmentId]);

  // Cascade: filter assignedTo by selected department(s), mark inactive
  // Uses salesUsersData as base — guaranteed HRIS IDs that align with proposalRptUserLookup
  const cascadedProposalAssignedTo = useMemo(() => {
    const selectedDepts = (filterData.department || []).map(String);
    const source = salesUsersData.length ? salesUsersData : assignedToOptions;
    const base = source.map(u => {
      const userId = String(u.id ?? '');
      const entry = proposalRptUserLookup.get(userId);
      const name = u.name || u.label || '';
      return { id: userId, name: entry?.status === '0' ? `${name} (Inactive)` : name, departmentId: u.departmentId };
    });
    if (!selectedDepts.length) return base;
    return base.filter(u => {
      const entry = proposalRptUserLookup.get(u.id);
      return entry?.deptId != null && selectedDepts.includes(entry.deptId);
    });
  }, [filterData.department, salesUsersData, assignedToOptions, proposalRptUserLookup]);

  // createdBy shows all users regardless of selected department
  const cascadedProposalCreatedBy = useMemo(() => {
    const source = salesUsersData.length ? salesUsersData : (filterOptions.createdByUsers || []);
    return source.map(u => {
      const userId = String(u.id ?? '');
      const entry = proposalRptUserLookup.get(userId);
      const name = u.name || u.label || '';
      return { id: userId, name: entry?.status === '0' ? `${name} (Inactive)` : name };
    });
  }, [salesUsersData, filterOptions.createdByUsers, proposalRptUserLookup]);

  // filterFields config for AdvancedFilterPanel
  const proposalReportFilterFields = useMemo(() => {
    const fields = [];
    fields.push(
      { label: 'Proposal Date', type: 'dateRange', fromKey: 'proposalDateFrom', toKey: 'proposalDateTo' },
      { label: 'Valid Until', type: 'dateRange', fromKey: 'validUntilFrom', toKey: 'validUntilTo' },
      { label: 'Stage Update Date', type: 'dateRange', fromKey: 'stageUpdateFrom', toKey: 'stageUpdateTo' },
      { label: 'Updated At', type: 'dateRange', fromKey: 'updatedAtFrom', toKey: 'updatedAtTo' }
    );
    // Show assignedTo/createdBy/department only when user has view_global or view_department
    if (canViewGlobal || canViewDepartment) {
      fields.push(
        { key: 'department', label: 'Department', type: 'multiselect', options: businessUnitsData, valueField: 'id', labelField: 'name' },
        { key: 'assignedTo', label: 'Assigned To', type: 'multiselect', options: cascadedProposalAssignedTo, valueField: 'id', labelField: 'name' },
        { key: 'createdBy', label: 'Created By', type: 'multiselect', options: cascadedProposalCreatedBy, valueField: 'id', labelField: 'name' }
      );
    }
    fields.push(
      { key: 'sector', label: 'Sector', type: 'multiselect', options: filterOptions.sectors || [], valueField: 'id', labelField: 'name' },
      { key: 'standards', label: 'Standard', type: 'multiselect', options: filterOptions.standards || [], valueField: 'id', labelField: 'name' },
      { key: 'service', label: 'Service', type: 'multiselect', options: servicesData, valueField: 'id', labelField: 'name' },
      { key: 'source', label: 'Source', type: 'multiselect', options: sourcesData, valueField: 'id', labelField: 'name' },
      { key: 'proposalStage', label: 'Proposal Stage', type: 'multiselect', options: filterOptions.proposalStages || [], valueField: 'id', labelField: 'name' },
      {
        key: 'approvalStatus', label: 'Approval Status', type: 'multiselect',
        options: PROPOSALS_REPORT_APPROVAL_STATUS_OPTIONS.map(o => ({ value: o.id, label: o.name })),
        valueField: 'value', labelField: 'label'
      },
      {
        key: 'proposalStatus', label: 'Proposal Status', type: 'multiselect',
        options: PROPOSALS_REPORT_PROPOSAL_STATUS_OPTIONS.map(o => ({ value: o.id, label: o.name })),
        valueField: 'value', labelField: 'label'
      },
      {
        key: 'projectStatus', label: 'Project Status', type: 'select',
        options: [
          { value: 'converted', label: 'Converted' },
          { value: 'not converted', label: 'Not Converted' }
        ]
      },
      { key: 'branchId', label: 'Branch', type: 'multiselect', options: branches || [], valueField: 'id', labelField: 'name' },
      {
        key: 'taggingIds', label: 'Tagging', type: 'multiselect',
        options: [
          ...(taggingOptions || []).map(t => ({ id: String(t.id), name: `${t.name} (Company)` })),
          ...(proposalTaggingOptions || []).map(t => ({ id: `proposal-${t.id}`, name: `${t.name} (Proposal)` }))
        ],
        valueField: 'id', labelField: 'name'
      },
    );
    return fields;
  }, [filterOptions, assignedToOptions, cascadedProposalAssignedTo, cascadedProposalCreatedBy, proposalRptUserLookup, businessUnitsData, sourcesData, servicesData, canViewGlobal, canViewDepartment, branches, taggingOptions, proposalTaggingOptions]);

  // Active filter count and tags for FilterAwarenessBar
  const activeFilterCount = getActiveFilterCount(appliedFilters, proposalReportFilterFields);

  const proposalFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({ key: 'period', label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}` });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({ key: 'period', label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}` });
    }

    proposalReportFilterFields.forEach((field) => {
      if (field.type === 'dateRange') {
        if (appliedFilters[field.fromKey] || appliedFilters[field.toKey]) {
          tags.push({ key: field.fromKey, label: `${field.label}: ${[appliedFilters[field.fromKey], appliedFilters[field.toKey]].filter(Boolean).join(' - ')}` });
        }
        return;
      }
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }
      const option = (field.options || []).find(opt => String(opt[field.valueField || 'value']) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option[field.labelField || 'label'] : value}` });
    });

    return tags;
  }, [appliedFilters, proposalReportFilterFields]);

  // Chip remove handler
  const handleChipRemove = (key) => {
    const isPeriod = ['period', 'startDate', 'endDate'].includes(key);
    const keysToClear = isPeriod ? ['period', 'startDate', 'endDate'] : [key];
    const nextFilters = { ...appliedFilters };
    keysToClear.forEach(k => {
      const field = proposalReportFilterFields.find(f => f.key === k || f.fromKey === k || f.toKey === k);
      nextFilters[k] = (field && Array.isArray(appliedFilters[k])) ? [] : '';
    });
    setFilterData(prev => {
      const n = { ...prev };
      keysToClear.forEach(k => { n[k] = Array.isArray(prev[k]) ? [] : ''; });
      return n;
    });
    setAppliedFilters(nextFilters);
    setPage(0);
    persistState({ filters: nextFilters, search: searchQuery, page: 0, size });
    loadProposals(0, size, sortConfig, nextFilters);
  };

  const handleExportExcel = () => {
    if (!canProposal('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }
    try {
      // Filter data based on search query
      const dataToExport = searchQuery
        ? proposalsData.filter(item =>
          item.proposal?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.sectorName || item.sector || item.sector?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
        )
        : proposalsData;

      // Prepare data for Excel export
      const excelData = dataToExport.map(row => ({
        'Proposal': row.proposal || '',
        'Client': row.client || '',
        'Sector': row.sectorName || row.sector || row.sector?.name || '-',
        'Standard': row.standardName || row.standard || '-',
        'Proposal Date': row.proposalDate ? formatDateDDMMYYYY(row.proposalDate) : '',
        'Valid Until': row.validUntil ? formatDateDDMMYYYY(row.validUntil) : '',
        'Partner Type': row.partnerType || '',
        // Amount is already converted to the IDR base currency during mapping.
        'Amount': formatAmount(row.amount, 'IDR'),
        'Created By': `${row.createdBy?.firstName || ''} ${row.createdBy?.lastName || ''}`.trim() || '-',
        'Department': row.department?.name || 'N/A',
        'Assigned To': `${row.assignedTo?.firstName || ''} ${row.assignedTo?.lastName || ''}`.trim() || '-',
        'Created At': row.createdAt ? formatDateDDMMYYYY(row.createdAt) : '',
        'Update Date': (() => {
          const dateStr = row.updatedAt || row.udate || row.updated_at;
          return dateStr ? formatDateDDMMYYYY(dateStr) : '-';
        })(),
        'Status Approval': row.statusDesc || 'Draft',
        'Status Proposal': row.statusProposalDesc || 'Draft',
        'Project Status': row.projectStatus || 'Not Converted',
        'Proposal Stage': getProposalStageName(row),
        'Stage Updated': (() => {
          const dateStr = row.stageUpdatedAt || row.stage_updated_at;
          return dateStr ? formatDateDDMMYYYY(dateStr) : '-';
        })()
      }));

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // Proposal
        { wch: 30 }, // Client
        { wch: 20 }, // Sector
        { wch: 20 }, // Standard
        { wch: 15 }, // Proposal Date
        { wch: 15 }, // Valid Until
        { wch: 20 }, // Partner Type
        { wch: 20 }, // Amount
        { wch: 20 }, // Created By
        { wch: 20 }, // Department
        { wch: 20 }, // Assigned To
        { wch: 15 }, // Created At
        { wch: 15 }, // Update Date
        { wch: 18 }, // Status Approval
        { wch: 18 }, // Status Proposal
        { wch: 18 }, // Project Status
        { wch: 18 }, // Proposal Stage
        { wch: 16 }  // Stage Updated
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Proposals Report');

      // Generate file name with timestamp
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `proposals_report_${timestamp}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, fileName);

      showAlert('success', 'Proposals report exported to Excel successfully!');
    } catch (e) {
      console.error('Export Excel failed:', e);
      showAlert('danger', 'Failed to export proposals report to Excel. Please try again.');
    }
  };

  const columns = [
    {
      key: 'proposal',
      label: 'Proposal',
      className: 'text-truncate',
      render: (value, row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/proposals/detail/${row.leadId}`);
          }}
          className="text-primary text-decoration-none text-truncate d-inline-block"
          style={{ cursor: 'pointer', maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </a>
      )
    },
    {
      key: 'client',
      label: 'Client',
      className: 'text-truncate',
      sortValue: (item) => item.client || item.companyName || '',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'sector',
      label: 'Sector',
      className: 'text-truncate',
      sortValue: (item) => item.sectorName || item.sector || item.sector?.name || '',
      render: (value, item) => {
        const sectorValue = item.sectorName || item.sector || item.sector?.name || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '180px', width: '180px' }}
            title={sectorValue}
          >
            {sectorValue}
          </span>
        );
      }
    },
    {
      key: 'standardName',
      label: 'Standard',
      className: 'text-truncate',
      render: (value, item) => {
        const standardValue = value || item.standardName || item.standard || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={standardValue}
          >
            {standardValue}
          </span>
        );
      }
    },
    {
      key: 'proposalDate',
      label: 'Proposal Date',
      type: 'date',
      className: 'text-truncate',
      render: (value) => {
        const dateValue = value ? formatDateDDMMYYYY(value) : '';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      type: 'date',
      className: 'text-truncate',
      render: (value) => {
        const dateValue = value ? formatDateDDMMYYYY(value) : '';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'partnerType',
      label: 'Partner Type',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      className: 'text-truncate',
      render: (value, item) => {
        // `value` is IDR-converted, matching Proposals/index.jsx.
        const formattedValue = formatAmount(value, 'IDR');
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={formattedValue}
          >
            {formattedValue}
          </span>
        );
      }
    },
    {
      key: 'createdBy',
      label: 'Created By',
      className: 'text-truncate',
      sortValue: (item) => `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'department',
      label: 'Department',
      className: 'text-truncate',
      sortValue: (item) => item.department?.name || 'N/A',
      render: (value, item) => {
        const deptName = item.department?.name || 'N/A';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={deptName}
          >
            {deptName}
          </span>
        );
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      className: 'text-truncate',
      sortValue: (item) => `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'date',
      className: 'text-truncate',
      render: (value) => {
        const dateValue = value ? formatDateDDMMYYYY(value) : '';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'updatedAt',
      label: 'Update Date',
      type: 'date',
      className: 'text-truncate',
      render: (value, item) => {
        // Check multiple possible field names for update date
        const dateStr = value || item.udate || item.updated_at || item.updatedAt || item.stageUpdatedAt;
        if (!dateStr || dateStr === '-') return '-';
        try {
          const dateValue = formatDateDDMMYYYY(dateStr);
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '150px', width: '150px' }}
              title={dateValue}
            >
              {dateValue}
            </span>
          );
        } catch (e) {
          return dateStr || '-';
        }
      }
    },
    {
      key: 'statusDesc',
      label: 'Status Approval',
      className: 'text-truncate',
      render: (value) => {
        const statusValue = value || 'Draft';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={statusValue}
          >
            {statusValue}
          </span>
        );
      }
    },
    {
      key: 'statusProposalDesc',
      label: 'Status Proposal',
      className: 'text-truncate',
      render: (value, row) => {
        const proposalStatus = value || row.statusProposalDesc || 'Draft';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={proposalStatus}
          >
            {proposalStatus}
          </span>
        );
      }
    },
    {
      key: 'projectStatus',
      label: 'Project Status',
      className: 'text-truncate',
      render: (value, row) => {
        const projectStatus = value || row.projectStatus || 'Not Converted';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={projectStatus}
          >
            {projectStatus}
          </span>
        );
      }
    },
    {
      key: 'proposalStage',
      label: 'Proposal Stage',
      className: 'text-truncate',
      sortValue: (row) => getProposalStageName(row),
      render: (value, row) => {
        const proposalStageName = getProposalStageName(row);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={proposalStageName}
          >
            {proposalStageName}
          </span>
        );
      }
    },
    {
      key: 'stageUpdatedAt',
      label: 'Stage Updated',
      type: 'date',
      className: 'text-truncate',
      render: (value, item) => {
        const dateStr = value || item.stageUpdatedAt || item.stage_updated_at;
        if (!dateStr || dateStr === '-') return '-';
        try {
          const dateValue = formatDateDDMMYYYY(dateStr);
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '150px', width: '150px' }}
              title={dateValue}
            >
              {dateValue}
            </span>
          );
        } catch (e) {
          return dateStr || '-';
        }
      }
    }
  ];

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col-md-3">
          <h3 className="page-title m-0">Proposals Reports</h3>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      paginationTopContent={
                        <div className="row">
                          <div className="col-12">
                            <div className="row mb-3">
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total records for this page:</strong>
                                  <span>{proposalsData.length}</span>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total Amount (This Page):</strong>
                                  <span>{formatCurrency(totalSums.pageTotal, proposalCurrencyCode)}</span>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total Records:</strong>
                                  <span>{totalElements}</span>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total Amount:</strong>
                                  <span>{formatCurrency(totalSums.totalAmount, proposalCurrencyCode)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      data={proposalsData}
                      columns={columns}
                      isLoading={isLoading}
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onSearchSubmit={() => setPage(0)}
                          placeholder="Search proposals..."
                        />
                      }
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasProposalsReport" activeCount={activeFilterCount} />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canProposal('export_data') ? handleExportExcel : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Proposals Report is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={proposalFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={isLoading}
                          activeCount={activeFilterCount}
                          filterTags={proposalFilterTags}
                          onRemoveFilter={handleChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: searchQuery, page: nextPage, size });
                          loadProposals(nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
                          loadProposals(0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        loadProposals(0, size, newSortConfig);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Advanced Filter Panel */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasProposalsReport"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={proposalReportFilterFields}
      />
    </div>
  );
};

export default ProposalsReports;
